import DOMPurify from "dompurify";

/**
 * SP6 (C3) — Send-Sanitizing + Inline-Bild-Helfer für das Verfassen-Fenster.
 *
 * `sanitizeComposeHtml` ist die FE-Defense-in-Depth: Der Compose-`innerHTML`
 * wird vor dem Senden gesäubert (der Server sanitisiert autoritativ erneut über
 * `mail-html-sanitizer.ts`). Die FORBID-Liste spiegelt die Lese-Pipeline
 * (`SandboxedMailBody.tsx`) — `<script>/<iframe>/<object>/<embed>/<form>` und
 * `on*`-Handler raus.
 *
 * Anders als die Lese-Pipeline ist dies der AUSGEHENDE Pfad: `cid:`- und
 * `data:`-`img[src]` MÜSSEN erhalten bleiben (sie tragen die Inline-Bilder).
 * `javascript:`-URLs werden trotzdem entfernt.
 *
 * Wiederverwendbar durch SP2 (Reply-Quote-Sanitizing) und SP7 (Template-Insert).
 */

// Erlaubte URI-Schemata inkl. `cid:` und `data:image/...` (für Inline-Bilder).
// `javascript:`/`vbscript:` etc. matchen NICHT → werden von DOMPurify entfernt.
const COMPOSE_URI_REGEXP =
  /^(?:(?:https?|mailto|tel|cid):|data:image\/(?:png|jpeg|gif|webp);base64,|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

export function sanitizeComposeHtml(html: string): string {
  return DOMPurify.sanitize(html ?? "", {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ["script", "iframe", "object", "embed", "form"],
    FORBID_ATTR: ["onload", "onerror", "onclick", "onmouseover", "formaction"],
    // `cid:`/`data:image`-Bild-Quellen durchlassen (Inline-Bilder).
    ALLOWED_URI_REGEXP: COMPOSE_URI_REGEXP,
    // Unsere Tracking-Markierungen erhalten: `data-cid` am <img> (Inline-Bilder),
    // `data-kmvs-quote`/`data-kmvs-signature` am zitierten Verlauf bzw. an der
    // eingefügten Signatur (SP2/SP5). DOMPurify erlaubt `data-*` zwar per Default
    // (`ALLOW_DATA_ATTR`); die explizite Nennung verankert die Garantie im Code,
    // statt sie von einem Bibliotheks-Default abhängig zu machen. Server-seitig
    // werden diese Attribute bewusst NICHT erlaubt (`mail-html-sanitizer.ts` lässt
    // nur style/class/dir/lang durch) — der Marker verlässt damit nie den Client
    // und kann nicht über ein zitiertes Fremd-HTML zurückkommen.
    ADD_ATTR: ["data-cid", "data-kmvs-quote", "data-kmvs-signature"],
  });
}

/**
 * Liest eine Datei als Base64 OHNE `data:...;base64,`-Präfix (für den
 * Attachment-Wire-Shape). Spiegelt das `readAsDataUrl`-Muster aus
 * `FileDropzone.tsx`, schneidet aber den `data:`-Präfix ab.
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = String(r.result ?? "");
      const comma = result.indexOf(",");
      // `data:<mime>;base64,<payload>` → nur den Payload.
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    r.onerror = () => reject(r.error ?? new Error("Datei konnte nicht gelesen werden"));
    r.readAsDataURL(file);
  });
}

/** Inline-Bilder: erlaubte MIME-Typen (KEIN `image/svg+xml` — XSS-Schutz). */
export const INLINE_IMAGE_ALLOWED_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
] as const;

/**
 * Maximalgröße pro Inline-Bild (binär).
 *
 * Mit Rechenweg, weil eine glatte Zahl hier schon einmal einen echten Fehler
 * verdeckt hat: base64 bläht auf `4·⌈n/3⌉` Zeichen auf, ein Bild darf also nie
 * so groß gewählt werden, dass es `ATTACHMENT_MAX_CHARS` (= `@MaxLength` auf
 * `contentBytes`, `apps/api/src/mail/mail.dto.ts`) verletzt. Ein glattes 2 MiB
 * wäre genau diese Falle: 2.097.152 Byte → 2.796.204 Zeichen, also ÜBER der
 * Grenze. 1900 KiB (1.945.600 Byte) → 2.594.136 Zeichen liegen sicher darunter.
 * Ein Test hält den Zusammenhang fest (composeLimits.test.ts).
 */
export const INLINE_IMAGE_MAX_BYTES = 1900 * 1024; // 1.945.600 Byte → 2.594.136 base64-Zeichen

/**
 * Obergrenze für die tatsächliche Größe des Sende-Requests, gemessen in Zeichen
 * (base64-Anhänge + UTF-8-kodierter Body).
 *
 * Bewusst in ZEICHEN, nicht in Byte: der frühere Binär-Guard maß die binäre
 * Summe und ließ damit Requests durch, die über die Leitung ~33 % größer sind.
 *
 * 30.000.000 Zeichen (≈ 22,5 MB binär). Bis 09/2026 waren es 3.400.000 (≈ 2,5
 * MB) — die Grenze kam von Microsoft Graph, das in EINER Anfrage nur rund 4 MB
 * annimmt. Seit dem Entwurfs-Weg (`sendMailViaDraft` im Backend) gilt das nicht
 * mehr: Große Nachrichten entstehen als Entwurf, ihre Anhänge wandern
 * stückweise hinein, und erst danach wird gesendet.
 *
 * Was die Zahl jetzt bestimmt, steht außerhalb dieses Codes: Exchange Online
 * lässt üblich 35 MB je Nachricht durch (codiert — aus 22,5 MB Rohdaten werden
 * rund 30 MB), und der Server des Empfängers riegelt oft schon bei 25 MB ab.
 * Der Abstand nach oben ist Absicht: Lieber eine Mail, die ankommt, als eine,
 * die der Empfänger nie sieht.
 */
export const SEND_PAYLOAD_MAX_CHARS = 30_000_000;

/**
 * Maximale base64-Länge EINES Anhangs — spiegelt `@MaxLength` auf
 * `contentBytes` (`apps/api/src/mail/mail.dto.ts`).
 *
 * 28.000.000 Zeichen sind 21 MB binär — dem Nutzer gegenüber „20 MB", mit
 * etwas Luft für die Rundung. Der Abstand zu `SEND_PAYLOAD_MAX_CHARS` ist kein
 * Zufall: Die größtmögliche Signatur (500 KiB Bilder → 682.668 base64-Zeichen)
 * reist bei JEDER Mail mit und muss neben dem größten Anhang noch in die
 * Gesamtgrenze passen. Ein größerer Wert hier hieße, dass ein maximaler Anhang
 * zusammen mit einer maximalen Signatur am Gesamt-Guard scheitert — für den
 * Nutzer nicht nachvollziehbar. Ein Test hält das fest (composeLimits.test.ts).
 */
export const ATTACHMENT_MAX_CHARS = 28_000_000;

/** base64-Zeichen → binäre Byte (ohne zu dekodieren). */
export const base64CharsToBytes = (chars: number): number => Math.floor((chars * 3) / 4);

/** Binäre Byte → base64-Zeichen (`4·⌈n/3⌉`). */
export const bytesToBase64Chars = (bytes: number): number => 4 * Math.ceil(bytes / 3);

/** Kompakte Größenangabe für Meldungen und Anhang-Chips: „1,4 MB" / „312 KB". */
export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

/**
 * Erlaubte Anhang-MIME-Typen — spiegelt `ALLOWED_ATTACHMENT_MIME_TYPES`
 * (`apps/api/src/mail/mail.dto.ts`). SVG fehlt hier bewusst: das Backend lehnt
 * es hart ab (XSS via Script-in-SVG). Ein leerer `file.type` (Browser kennt die
 * Endung nicht) wird über die Endung aufgelöst (`anhangMimeTyp`).
 *
 * T-224: Mail-Dateien (`.msg` aus Outlook, `.eml`) gehören dazu — eine Mail
 * als Anhang weiterzuschicken ist Makler-Alltag. In KMVS werden sie nicht
 * inline dargestellt, das SVG-Argument trifft auf sie also nicht zu.
 *
 * T-341: ZIP-Archive (`application/zip`, unter Windows/Edge
 * `application/x-zip-compressed`), um viele Anhänge gebündelt zu verschicken.
 * Auch ZIP wird in KMVS nie inline gezeigt; Exchange Online scannt die Archive.
 * Andere Archivformate (`.7z`, `.rar`) bewusst nicht.
 */
export const ALLOWED_ATTACHMENT_MIME_TYPES: readonly string[] = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/msword",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint",
  "text/plain",
  "text/csv",
  "application/vnd.ms-outlook",
  "message/rfc822",
  "application/zip",
  "application/x-zip-compressed",
];

/**
 * MIME-Typ je Dateiendung — für Dateien, deren Typ der Browser nicht kennt
 * (`file.type` leer). Unter Windows liefert Edge für eine `.msg` nur dann einen
 * Typ, wenn Outlook installiert ist; ohne ihn scheiterte die Datei früher erst
 * beim Senden am Server (400), obwohl sie längst als Anhang dastand (T-224).
 */
const MIME_NACH_ENDUNG: Readonly<Record<string, string>> = {
  msg: "application/vnd.ms-outlook",
  eml: "message/rfc822",
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  doc: "application/msword",
  xls: "application/vnd.ms-excel",
  ppt: "application/vnd.ms-powerpoint",
  txt: "text/plain",
  csv: "text/csv",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  zip: "application/zip",
};

/**
 * Der MIME-Typ, mit dem eine Datei als Anhang reist: der des Browsers, wenn er
 * einen kennt, sonst der zur Endung — und erst dann `application/octet-stream`
 * (den das Backend ablehnt; `attachmentRejectReason` meldet das schon beim
 * Auswählen).
 */
export function anhangMimeTyp(file: { name: string; type: string }): string {
  if (file.type) return file.type;
  const name = (file.name ?? "").toLowerCase();
  const punkt = name.lastIndexOf(".");
  const endung = punkt >= 0 ? name.slice(punkt + 1) : "";
  return MIME_NACH_ENDUNG[endung] ?? "application/octet-stream";
}

/**
 * Prüft eine ausgewählte Datei, BEVOR sie in die Anhangsliste wandert.
 * Liefert `null`, wenn sie in Ordnung ist, sonst die Meldung für den User.
 */
export function attachmentRejectReason(file: { name: string; size: number; type: string }): string | null {
  if (bytesToBase64Chars(file.size) > ATTACHMENT_MAX_CHARS) {
    return `„${file.name}" ist zu groß (${formatBytes(file.size)}, max. ${formatBytes(
      base64CharsToBytes(ATTACHMENT_MAX_CHARS),
    )}).`;
  }
  const typ = anhangMimeTyp(file);
  if (!ALLOWED_ATTACHMENT_MIME_TYPES.includes(typ)) {
    return file.type
      ? `Dateityp von „${file.name}" ist nicht erlaubt (${file.type}).`
      : `Dateityp von „${file.name}" ist nicht erlaubt.`;
  }
  return null;
}

/**
 * Maximalgröße eines einzelnen Bildes in einer Signatur/Vorlage (binär).
 * Weiterhin enger als bei einer Mail: Signaturbilder sind Logos, und das HTML
 * wird bei JEDER Mail mitgesendet. FE und BE spiegeln diesen Wert (SP4/SP7).
 *
 * 1 MiB seit 21.09.2026 (vorher 500 KiB) — mit 500 KiB passte ein Logo in
 * heutiger Bildschirmauflösung oft nicht mehr hinein.
 */
export const SIGNATURE_IMAGE_MAX_BYTES = 1024 * 1024; // 1.048.576 Byte → 1.398.104 base64-Zeichen

/**
 * Obergrenze für ALLE eingebetteten Bilder einer Signatur/Vorlage zusammen
 * (binär). Bindend ist in der Praxis diese Summe — ein einzelnes Logo darf sie
 * nach SIGNATURE_IMAGE_MAX_BYTES komplett ausschöpfen.
 *
 * 1 MiB seit 21.09.2026 (PO-Vorgabe; vorher 500 KiB).
 */
export const SIGNATURE_EMBEDDED_TOTAL_MAX_BYTES = 1024 * 1024; // 1.048.576 Byte

/**
 * Maximale Zeichenzahl des gesamten Signatur-/Vorlagen-HTML — spiegelt
 * `@MaxLength` auf `html` in den vier Signatur- und Vorlagen-DTOs
 * (`apps/api/src/mail/mail.dto.ts`).
 *
 * Diese Zahl hängt AN {@link SIGNATURE_EMBEDDED_TOTAL_MAX_BYTES} und ist der
 * Grund, warum die Bildgrenze nie allein angehoben werden darf: Die Bilder
 * stecken als `data:`-URI im HTML, base64 bläht sie auf vier Drittel auf, und
 * dazu kommt das eigentliche Markup. Bei 1 MiB Bildern sind das schon
 * 1.398.104 Zeichen — mit den früheren 900.000 wäre jede Signatur, die die
 * neue Bildgrenze wirklich ausnutzt, beim Speichern abgewiesen worden, und
 * zwar mit „Die Signatur ist insgesamt zu groß", ohne dass jemand den
 * Zusammenhang zur Bildgrenze gesehen hätte. `composeLimits.test.ts` hält die
 * Kopplung fest.
 */
export const SIGNATURE_HTML_MAX_CHARS = 1_600_000;

/** Erzeugt eine stabile Content-Id (ohne Bindestriche), mit Fallback. */
export function newContentId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID().replace(/-/g, "");
  }
  return `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 10)}`;
}
